import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import React, {
  ChangeEvent,
  useEffect,
  useRef,
  useState,
  SetStateAction,
  Dispatch,
} from 'react';
import { QueryCache, useMutation, useQuery, useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';
import { MultilineTextField } from '../styles/Styles';
import { SwDatesPicker } from './dateSetting/SwDatesPicker';
import { SwSnackbar } from './views/SwSnackbar';
import { SwAlert } from './views/SwAlert';
import { RESULT_OK } from '../../common/resultCode';
import {
  MobileRegexp,
  commaFormat,
  educationOffices,
  getReadGoal,
  getSchoolClassCount,
  getSchoolGradeCount,
  getSchoolInSchoolGroup,
  getWalkGoal,
  schoolGroups,
} from '../../common/helper';
import { SwConfirmDialog } from './views/SwConfirmDialog';
import { schoolListState } from '../../common/atom';
import { useSession } from '../../hooks/session';
import { KEY_SCHOOL_LIST } from '../../common/key';

interface Props {
  open: boolean;
  onClose: () => void;
  isAdd: boolean;
  schoolClassInfo: any;
  onSave: (data: any) => void;
  onDelete: (data: any) => void;
}

const INIT_READ_GOAL = '3';
const INIT_WALK_GOAL = '7000';

export const SwSchoolClass: React.FC<Props> = ({
  open,
  onClose,
  isAdd,
  schoolClassInfo,
  onSave,
  onDelete,
}) => {
  const { loginVal } = useSession();
  const [schoolList, setSchoolList] = useState<any[]>([]);
  const [schools, setSchools] = useState<any[]>([]);
  const [educationOfficeSeq, setEducationOfficeSeq] = useState<number>(0);
  const [schoolSeq, setSchoolSeq] = useState<number>(0);
  const [schoolClassSeq, setSchoolClassSeq] = useState<number>(0);
  const [schoolGroupSeq, setSchoolGroupSeq] = useState<number>(1);
  const [schoolGrade, setSchoolGrade] = useState<number>(1);
  const [schoolClass, setSchoolClass] = useState<string>('1');
  const [customSchoolClass, setCustomSchoolClass] = useState<string>('');
  const [readGoal, setReadGoal] = useState<string>(INIT_READ_GOAL);
  const [walkGoal, setWalkGoal] = useState<string>(INIT_WALK_GOAL);
  const [schoolName, setSchoolName] = useState<string>('');
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<any>();
  const [alertTitle, setDialogTitle] = useState<string>('');
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<number>(0);

  const reloadSchools = (
    newSchoolList: any[],
    newSchoolGroupSeq: number,
    newEducationOfficeSeq: number
  ) => {
    const newSchools = getSchoolInSchoolGroup(
      newSchoolList,
      newSchoolGroupSeq,
      newEducationOfficeSeq
    );
    setSchools(newSchools);
  };

  const showError = (msg: string) => {
    setOpenSnackbar(true);
    setAlertMessage(msg);
  };

  const saveSchoolInfo = () => {
    if (readGoal === '-' || walkGoal === '-') {
      setOpenSnackbar(true);
      setDialogMessage(
        readGoal === '-'
          ? '읽기 목표를 지정해주시기 바랍니다.'
          : '걷기 목표를 지정해주시기 바랍니다.'
      );
      return;
    }
    if (schoolSeq === 0) {
      setOpenSnackbar(true);
      setDialogMessage('학교를 선택해주시기 바랍니다.');
      return;
    }
    const newItem: any = {
      schoolClassSeq: schoolClassSeq,
      educationOfficeSeq: educationOfficeSeq,
      schoolSeq: schoolSeq,
      schoolName: schoolName,
      schoolGroupSeq: schoolGroupSeq,
      schoolGrade: schoolGrade,
      schoolClass: schoolClass !== '0' ? schoolClass : customSchoolClass,
      readGoal: readGoal,
      walkGoal: walkGoal,
    };
    onSave(newItem);
    onClose();
  };

  const deleteSchoolInfo = () => {
    const newItem: any = {
      schoolClassSeq: schoolClassSeq,
    };
    onDelete(newItem);
    onClose();
  };

  useEffect(() => {
    reloadSchools(schoolList, schoolGroupSeq, educationOfficeSeq);
  }, [schoolGroupSeq, educationOfficeSeq]);

  useEffect(() => {
    if (open && !isAdd && schoolClassInfo) {
      setSchoolSeq(schoolClassInfo.schoolSeq);
      setSchoolClassSeq(schoolClassInfo.schoolClassSeq);
      setReadGoal(
        schoolClassInfo.readGoal !== null ? schoolClassInfo.readGoal : '-'
      );
      setWalkGoal(
        schoolClassInfo.walkGoal !== null ? schoolClassInfo.walkGoal : '-'
      );
      setEducationOfficeSeq(schoolClassInfo.educationOfficeSeq);
      setSchoolGroupSeq(schoolClassInfo.schoolGroupSeq);
      setSchoolName(schoolClassInfo.schoolName);
      setSchoolGrade(schoolClassInfo.schoolGrade);
      if (!Number.isNaN(Number(schoolClassInfo.schoolClass))) {
        setSchoolClass(schoolClassInfo.schoolClass);
      } else {
        setSchoolClass('0');
        setCustomSchoolClass(schoolClassInfo.schoolClass);
      }
    } else if (open && isAdd) {
      setEducationOfficeSeq(loginVal.value.user.educationOfficeSeq);
      setSchoolGroupSeq(loginVal.value.user.schoolGroupSeq);
      setSchoolSeq(loginVal.value.user.schoolSeq);
      setSchoolGrade(
        loginVal.value.user.schoolGrade !== 0
          ? loginVal.value.user.schoolGrade
          : 1
      );
      setSchoolName('');
      setSchoolClassSeq(0);
      setReadGoal(INIT_READ_GOAL);
      setWalkGoal(INIT_WALK_GOAL);
    } else {
      setSchoolSeq(0);
      setSchoolClassSeq(0);
      setReadGoal(INIT_READ_GOAL);
      setWalkGoal(INIT_WALK_GOAL);
      setEducationOfficeSeq(0);
      setSchoolGroupSeq(1);
      setSchoolName('');
      setSchoolGrade(1);
      setSchoolClass('1');
    }

    if (open) {
      const savedSchoolList = localStorage.getItem(KEY_SCHOOL_LIST);
      if (savedSchoolList) {
        const newSchoolList = JSON.parse(savedSchoolList);
        setSchoolList(newSchoolList);
      }
    }
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            minHeight: '56vh',
            minWidth: '24vw',
          },
        }}
      >
        <DialogTitle>
          <Box className='flex_between'>
            <Box>
              <Typography className='title'>
                {isAdd ? '데이터 추가' : '데이터 조회/수정'}
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider sx={{ m: 0, p: 0 }} />
        <DialogContent>
          <Box>
            <Typography className='title'>교육지원청</Typography>
            <Box className='flex_start'>
              <TextField
                disabled={!isAdd || loginVal.value.user.roleName === '관리자1'}
                sx={{ width: '15rem' }}
                select
                value={educationOfficeSeq}
                onChange={evt =>
                  setEducationOfficeSeq(Number(evt.target.value))
                }
              >
                <MenuItem key={`educatin_office_${0}`} value={0}>
                  선택
                </MenuItem>
                {educationOffices.map((item: any, idx: number) => (
                  <MenuItem key={`educatin_office_${idx}`} value={item.seq}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
          <Divider />
          <Box>
            <Typography className='title'>학교</Typography>
            <Box className='flex_start'>
              <TextField
                disabled={!isAdd || loginVal.value.user.roleName === '관리자1'}
                sx={{ width: '15rem' }}
                select
                value={schoolGroupSeq}
                onChange={evt => {
                  setSchoolGroupSeq(Number(evt.target.value));
                  setSchoolSeq(0);
                }}
              >
                <MenuItem key={`school_group_${0}`} value={0}>
                  초/중/고 선택
                </MenuItem>
                {schoolGroups.map((item: any, idx: number) => (
                  <MenuItem key={`school_group_${idx}`} value={item.seq}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                disabled={!isAdd || loginVal.value.user.roleName === '관리자1'}
                sx={{ width: '15rem', ml: '.5rem' }}
                select
                value={schoolSeq}
                onChange={evt => setSchoolSeq(Number(evt.target.value))}
              >
                <MenuItem key={`school_${0}`} value={0}>
                  학교 선택
                </MenuItem>
                {schools &&
                  schools.map((item: any, idx: number) => (
                    <MenuItem key={`school_${idx}`} value={item.schoolSeq}>
                      {item.schoolName}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>
          </Box>
          <Divider />
          <Box>
            <Typography className='title'>학년/반</Typography>
            <Box className='flex_start'>
              <TextField
                disabled={!isAdd}
                sx={{ width: '15rem', mr: '.5rem' }}
                select
                value={schoolGrade}
                onChange={evt => setSchoolGrade(Number(evt.target.value))}
              >
                {Array.from(
                  { length: getSchoolGradeCount(schoolGroupSeq) },
                  (_, index) => (
                    <MenuItem
                      key={`school_grade_${index + 1}`}
                      value={index + 1}
                    >
                      {`${index + 1} 학년`}
                    </MenuItem>
                  )
                )}
              </TextField>
              <TextField
                disabled={!isAdd}
                sx={{ width: '10rem' }}
                select
                value={schoolClass}
                onChange={evt => setSchoolClass(evt.target.value)}
              >
                <MenuItem key='school_class_0' value='0'>
                  직접 입력
                </MenuItem>
                {Array.from({ length: getSchoolClassCount() }, (_, index) => (
                  <MenuItem key={`school_class_${index + 1}`} value={index + 1}>
                    {`${index + 1} 반`}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            {schoolClass === '0' && (
              <TextField
                placeholder='반 이름 입력'
                sx={{ width: '15rem' }}
                value={customSchoolClass}
                onChange={evt => setCustomSchoolClass(evt.target.value)}
              />
            )}
          </Box>
          <Divider />
          <Box>
            <Typography className='title'>읽기 목표(월 완독 기준)</Typography>
            <Typography>
              <span style={{ color: '#2581FF' }}>금</span>{' '}
              {readGoal === '1'
                ? '1권 이상'
                : readGoal === '2'
                ? '2권 이상'
                : readGoal === '3'
                ? '3권이상'
                : readGoal === '5'
                ? '5권이상'
                : '-'}{' '}
              <span style={{ color: '#2581FF' }}>은</span>{' '}
              {readGoal === '1'
                ? '-'
                : readGoal === '2'
                ? '1권'
                : readGoal === '3'
                ? '2권'
                : readGoal === '5'
                ? '3~4권'
                : '-'}{' '}
              <span style={{ color: '#2581FF' }}>동</span>{' '}
              {readGoal === '1'
                ? '-'
                : readGoal === '2'
                ? '-'
                : readGoal === '3'
                ? '1권'
                : readGoal === '5'
                ? '1권'
                : '-'}
            </Typography>

            <TextField
              sx={{ width: '15rem', mt: '.5rem', mr: '.5rem' }}
              select
              value={readGoal}
              onChange={evt => setReadGoal(evt.target.value)}
            >
              <MenuItem key={`read_gole_${0}`} value='-'>
                미지정
              </MenuItem>
              {getReadGoal().map((item: string, index: number) => (
                <MenuItem key={`read_gole_${index + 1}`} value={item}>
                  {`${item}회 이상`}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Divider />
          <Box>
            <Typography className='title'>걷기 목표(일 기준)</Typography>
            <Typography>
              <span style={{ color: '#2581FF' }}>금</span>{' '}
              {walkGoal === '3000'
                ? '3,000보 이상'
                : walkGoal === '5000'
                ? '5,000보 이상'
                : walkGoal === '7000'
                ? '7,000보 이상'
                : walkGoal === '10000'
                ? '10,000보 이상'
                : walkGoal === '15000'
                ? '15,000보 이상'
                : '-'}{' '}
              <span style={{ color: '#2581FF' }}>은</span>{' '}
              {walkGoal === '3000'
                ? '2,000~2,999보'
                : walkGoal === '5000'
                ? '3,000~4,999보'
                : walkGoal === '7000'
                ? '5,000~6,999보'
                : walkGoal === '10000'
                ? '7,000~9,999보'
                : walkGoal === '15000'
                ? '10,000~14,999보'
                : '-'}{' '}
              <span style={{ color: '#2581FF' }}>동</span>{' '}
              {walkGoal === '3000'
                ? '1,000~1,999보'
                : walkGoal === '5000'
                ? '1,000~2,999보'
                : walkGoal === '7000'
                ? '1,000~4,999보'
                : walkGoal === '10000'
                ? '3,000~6,999보'
                : walkGoal === '15000'
                ? '5,000~9,999보'
                : '-'}{' '}
            </Typography>

            <TextField
              sx={{ width: '15rem', mt: '.5rem', mr: '.5rem' }}
              select
              value={walkGoal}
              onChange={evt => setWalkGoal(evt.target.value)}
            >
              <MenuItem key={`walk_gole_${0}`} value='-'>
                미지정
              </MenuItem>
              {getWalkGoal().map((item: string, index: number) => (
                <MenuItem key={`walk_gole_${index + 1}`} value={item}>
                  {`${commaFormat(item)}보 이상`}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <Divider sx={{ m: 0, p: 0 }} />
        <DialogActions>
          {isAdd ? (
            <Button
              variant='contained'
              color='info'
              sx={{ fontSize: '.87rem' }}
              onClick={saveSchoolInfo}
            >
              등록
            </Button>
          ) : (
            <Box>
              {loginVal.value.user.roleName === '마스터' && (
                <Button
                  variant='outlined'
                  color='info'
                  sx={{ fontSize: '.87rem', mr: '.5rem' }}
                  onClick={() => setOpenConfirm(2)}
                >
                  삭제
                </Button>
              )}
              <Button
                variant='contained'
                color='info'
                sx={{ fontSize: '.87rem' }}
                onClick={() => setOpenConfirm(1)}
              >
                저장
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>
      <SwSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        contents={dialogMessage}
      />
      <SwConfirmDialog
        contents={openConfirm === 1 ? '저장하시겠습니까?' : '삭제하시겠습니까?'}
        open={openConfirm !== 0}
        onClose={() => setOpenConfirm(0)}
        onConfirm={() => {
          openConfirm === 1 ? saveSchoolInfo() : deleteSchoolInfo();
          setOpenConfirm(0);
        }}
        confirm={1}
      />
    </>
  );
};
